type UnauthorizedSessionActions = {
  clearSession: () => void;
  redirect: (path: string) => void;
};

type ResponseInterceptorClient = {
  interceptors: {
    response: {
      eject: (interceptorId: number) => void;
      use: (
        onFulfilled: (response: unknown) => unknown,
        onRejected: (error: unknown) => Promise<never>,
      ) => number;
    };
  };
};

function hasBearerAuthorization(error: unknown): boolean {
  if (typeof error !== "object" || error === null) {
    return false;
  }

  const headers = (error as { config?: { headers?: unknown } }).config?.headers;
  if (typeof headers !== "object" || headers === null) {
    return false;
  }

  const headerCollection = headers as {
    Authorization?: unknown;
    authorization?: unknown;
    get?: (name: string) => unknown;
  };
  const authorization =
    headerCollection.Authorization ??
    headerCollection.authorization ??
    headerCollection.get?.("Authorization");

  return (
    typeof authorization === "string" && authorization.startsWith("Bearer ")
  );
}

export function isUnauthorizedError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) {
    return false;
  }

  const candidate = error as {
    response?: { status?: unknown };
    status?: unknown;
  };

  return candidate.status === 401 || candidate.response?.status === 401;
}

export function sanitizeInternalReturnTo(returnTo: string): string {
  const isInternalPath =
    returnTo.startsWith("/") &&
    !returnTo.startsWith("//") &&
    !returnTo.includes("\\") &&
    !/[\r\n]/.test(returnTo);

  return isInternalPath ? returnTo : "/";
}

export function buildUnauthorizedLoginPath(returnTo: string): string {
  const safeReturnTo = sanitizeInternalReturnTo(returnTo);

  return `/auth/login?returnTo=${encodeURIComponent(safeReturnTo)}`;
}

export function handleUnauthorizedSession(
  error: unknown,
  returnTo: string,
  actions: UnauthorizedSessionActions,
): boolean {
  if (!isUnauthorizedError(error)) {
    return false;
  }

  actions.clearSession();
  actions.redirect(buildUnauthorizedLoginPath(returnTo));
  return true;
}

export function installUnauthorizedResponseInterceptor(
  client: ResponseInterceptorClient,
  actions: UnauthorizedSessionActions & { getReturnTo: () => string },
): () => void {
  let redirectStarted = false;
  const interceptorId = client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (!redirectStarted && hasBearerAuthorization(error)) {
        redirectStarted = handleUnauthorizedSession(
          error,
          actions.getReturnTo(),
          actions,
        );
      }

      return Promise.reject(error);
    },
  );

  return () => client.interceptors.response.eject(interceptorId);
}
